<template>
  <div class="search-results-root" :style="themePageStyle">
  <head>
    <meta charset="utf-8" />
    <title>FLOCKR | Search Listings</title>
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <meta content="Search livestock listings across the region" name="description" />
    <meta content="Flockr" name="author" />
    <link rel="shortcut icon" href="/assets/images/favicon.svg">
    <link href="/assets/libs/datatables.net-bs4/css/dataTables.bootstrap4.min.css" rel="stylesheet" type="text/css" />
    <link href="/assets/libs/datatables.net-buttons-bs4/css/buttons.bootstrap4.min.css" rel="stylesheet" type="text/css" />
    <link href="/assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
    <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap-select@1.13.14/dist/css/bootstrap-select.min.css">
    <link href="/assets/css/icons.min.css" rel="stylesheet" type="text/css" />
    <link href="/assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
    <link href="/assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
  </head>

  <!-- was <body> — nested body breaks the DOM (document already has one) -->
  <div data-sidebar="dark">
  <div id="layout-wrapper">

    <!-- ========== TOP BAR ========== -->
    <nav class="sr-topbar">
      <div class="container d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center">
          <back-button class="me-2" />
          <a href="javascript:void(0)" @click="$router.push('/')" class="me-4">
            <img src="/assets/images/flockr_logo.png" alt="Flockr" style="height:34px;" />
          </a>
          <div class="sr-search-bar d-none d-md-flex">
            <i class="mdi mdi-magnify sr-search-icon"></i>
            <input v-model="filters.keyword" type="text" class="form-control sr-search-input"
                   placeholder="Search breed, species, location..."
                   @keyup.enter="applyFilters">
            <button class="btn btn-warning btn-sm text-dark fw-bold px-3" @click="applyFilters">Search</button>
          </div>
        </div>
        <div class="d-flex align-items-center" style="gap:12px;">
          <a href="javascript:void(0)" @click="$router.push('/marketplace')" class="sr-nav-link">Marketplace</a>
          <a href="javascript:void(0)" @click="$router.push('/sell/create')" class="btn btn-sm btn-outline-warning">
            <i class="mdi mdi-plus me-1"></i> Sell
          </a>
        </div>
      </div>
    </nav>

    <!-- ========== MOBILE SEARCH ========== -->
    <div class="sr-mobile-search d-md-none">
      <div class="container">
        <div class="input-group">
          <input v-model="filters.keyword" type="text" class="form-control sr-search-input-mobile"
                 placeholder="Search..." @keyup.enter="applyFilters">
          <button class="btn btn-warning text-dark fw-bold" @click="applyFilters"><i class="mdi mdi-magnify"></i></button>
        </div>
      </div>
    </div>

    <!-- ========== MAIN CONTENT ========== -->
    <div class="container py-4">
      <div class="row">

        <!-- ===== FILTERS SIDEBAR ===== -->
        <div class="col-lg-3">
          <!-- Mobile filter toggle -->
          <button class="btn btn-outline-warning w-100 mb-3 d-lg-none" @click="showFilters = !showFilters">
            <i class="mdi mdi-filter-variant me-1"></i> {{ showFilters ? 'Hide Filters' : 'Show Filters' }}
            <span v-if="activeFilterCount > 0" class="badge bg-warning text-dark ms-1">{{ activeFilterCount }}</span>
          </button>

          <div class="sr-filters-panel" :class="{ 'd-none d-lg-block': !showFilters }">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h6 class="fw-bold mb-0" style="color:var(--fm-h,#F5F0E8);"><i class="mdi mdi-filter-variant me-1"></i> Filters</h6>
              <button class="btn btn-link btn-sm text-muted p-0" @click="clearAllFilters" v-if="activeFilterCount > 0">Clear All</button>
            </div>

            <!-- What are you shopping for? Live animals vs livestock products (Dev-Requests #4). -->
            <div class="sr-filter-group">
              <div class="sr-filter-title" @click="toggleFilter('kind')">
                Category <i :class="openFilters.kind ? 'mdi mdi-chevron-up' : 'mdi mdi-chevron-down'"></i>
              </div>
              <div v-if="openFilters.kind" class="sr-filter-body">
                <label class="sr-filter-radio">
                  <input type="radio" value="" v-model="filters.listingKind" @change="onKindChange">
                  <span>All</span>
                </label>
                <label class="sr-filter-radio">
                  <input type="radio" value="live_animal" v-model="filters.listingKind" @change="onKindChange">
                  <span>🐄 Live animals</span>
                </label>
                <label class="sr-filter-radio">
                  <input type="radio" value="livestock_product" v-model="filters.listingKind" @change="onKindChange">
                  <span>🥛 Livestock products <small style="color:var(--fm-s,#6B6356);">(milk, eggs, hides, meat…)</small></span>
                </label>
              </div>
            </div>

            <!-- Product category — only when livestock_product is picked -->
            <div class="sr-filter-group" v-if="filters.listingKind === 'livestock_product'">
              <div class="sr-filter-title" @click="toggleFilter('productCategory')">
                Product Type <i :class="openFilters.productCategory ? 'mdi mdi-chevron-up' : 'mdi mdi-chevron-down'"></i>
              </div>
              <div v-if="openFilters.productCategory" class="sr-filter-body">
                <label class="sr-filter-radio" v-for="cat in productCategories" :key="'pc-'+cat.id">
                  <input type="checkbox" :value="cat.id" v-model="filters.productCategoryIds">
                  <span>{{ cat.name }}</span>
                </label>
              </div>
            </div>

            <!-- Species — hidden when browsing livestock products (species doesn't apply) -->
            <div class="sr-filter-group" v-if="filters.listingKind !== 'livestock_product'">
              <div class="sr-filter-title" @click="toggleFilter('species')">
                Species <i :class="openFilters.species ? 'mdi mdi-chevron-up' : 'mdi mdi-chevron-down'"></i>
              </div>
              <div v-if="openFilters.species" class="sr-filter-body">
                <div v-if="loadingSpecies" class="text-center py-2">
                  <div class="spinner-border spinner-border-sm text-warning"></div>
                </div>
                <div v-else>
                  <label class="sr-filter-option" v-for="sp in speciesList" :key="sp.id">
                    <input type="checkbox" :value="sp.id" v-model="filters.species"> {{ sp.name }}
                    <span class="sr-filter-count">{{ sp.listing_count || 0 }}</span>
                  </label>
                </div>
              </div>
            </div>

            <!-- Breed -->
            <div class="sr-filter-group">
              <div class="sr-filter-title" @click="toggleFilter('breed')">
                Breed <i :class="openFilters.breed ? 'mdi mdi-chevron-up' : 'mdi mdi-chevron-down'"></i>
              </div>
              <div v-if="openFilters.breed" class="sr-filter-body">
                <div v-if="loadingBreeds" class="text-center py-2">
                  <div class="spinner-border spinner-border-sm text-warning"></div>
                </div>
                <div v-else-if="breedsList.length === 0" class="text-muted" style="font-size:13px;">Select a species first</div>
                <div v-else>
                  <label class="sr-filter-option" v-for="br in breedsList" :key="br.id">
                    <input type="checkbox" :value="br.id" v-model="filters.breeds"> {{ br.name }}
                  </label>
                </div>
              </div>
            </div>

            <!-- Price Range -->
            <div class="sr-filter-group">
              <div class="sr-filter-title" @click="toggleFilter('price')">
                Price Range (KES) <i :class="openFilters.price ? 'mdi mdi-chevron-up' : 'mdi mdi-chevron-down'"></i>
              </div>
              <div v-if="openFilters.price" class="sr-filter-body">
                <div class="row g-2">
                  <div class="col-6">
                    <input type="number" class="form-control form-control-sm sr-filter-input" placeholder="Min" v-model="filters.priceMin">
                  </div>
                  <div class="col-6">
                    <input type="number" class="form-control form-control-sm sr-filter-input" placeholder="Max" v-model="filters.priceMax">
                  </div>
                </div>
                <div class="d-flex flex-wrap mt-2" style="gap:4px;">
                  <button class="btn btn-sm sr-price-chip" @click="setPriceRange(0, 10000)">Under 10K</button>
                  <button class="btn btn-sm sr-price-chip" @click="setPriceRange(10000, 50000)">10K–50K</button>
                  <button class="btn btn-sm sr-price-chip" @click="setPriceRange(50000, 100000)">50K–100K</button>
                  <button class="btn btn-sm sr-price-chip" @click="setPriceRange(100000, 500000)">100K–500K</button>
                  <button class="btn btn-sm sr-price-chip" @click="setPriceRange(500000, null)">500K+</button>
                </div>
              </div>
            </div>

            <!-- Sex -->
            <div class="sr-filter-group">
              <div class="sr-filter-title" @click="toggleFilter('sex')">
                Sex <i :class="openFilters.sex ? 'mdi mdi-chevron-up' : 'mdi mdi-chevron-down'"></i>
              </div>
              <div v-if="openFilters.sex" class="sr-filter-body">
                <label class="sr-filter-option">
                  <input type="radio" value="" v-model="filters.sex"> Any
                </label>
                <label class="sr-filter-option">
                  <input type="radio" value="male" v-model="filters.sex"> Male
                </label>
                <label class="sr-filter-option">
                  <input type="radio" value="female" v-model="filters.sex"> Female
                </label>
                <label class="sr-filter-option">
                  <input type="radio" value="mixed" v-model="filters.sex"> Mixed
                </label>
              </div>
            </div>

            <!-- Age Range -->
            <div class="sr-filter-group">
              <div class="sr-filter-title" @click="toggleFilter('age')">
                Age (months) <i :class="openFilters.age ? 'mdi mdi-chevron-up' : 'mdi mdi-chevron-down'"></i>
              </div>
              <div v-if="openFilters.age" class="sr-filter-body">
                <div class="d-flex align-items-center gap-2">
                  <input type="number" class="form-control form-control-sm sr-filter-input" placeholder="Min" v-model="filters.ageMin" min="0">
                  <span style="color:var(--fm-s,#6B6356);">–</span>
                  <input type="number" class="form-control form-control-sm sr-filter-input" placeholder="Max" v-model="filters.ageMax" min="0">
                </div>
                <div class="d-flex flex-wrap gap-1 mt-2">
                  <button class="btn btn-sm sr-price-chip" @click="filters.ageMin='0';filters.ageMax='6'">0–6mo</button>
                  <button class="btn btn-sm sr-price-chip" @click="filters.ageMin='6';filters.ageMax='12'">6–12mo</button>
                  <button class="btn btn-sm sr-price-chip" @click="filters.ageMin='12';filters.ageMax='24'">1–2yr</button>
                  <button class="btn btn-sm sr-price-chip" @click="filters.ageMin='24';filters.ageMax='48'">2–4yr</button>
                  <button class="btn btn-sm sr-price-chip" @click="filters.ageMin='48';filters.ageMax=''">4yr+</button>
                </div>
              </div>
            </div>

            <!-- Weight Range -->
            <div class="sr-filter-group">
              <div class="sr-filter-title" @click="toggleFilter('weight')">
                Weight (kg) <i :class="openFilters.weight ? 'mdi mdi-chevron-up' : 'mdi mdi-chevron-down'"></i>
              </div>
              <div v-if="openFilters.weight" class="sr-filter-body">
                <div class="d-flex align-items-center gap-2">
                  <input type="number" class="form-control form-control-sm sr-filter-input" placeholder="Min" v-model="filters.weightMin" min="0">
                  <span style="color:var(--fm-s,#6B6356);">–</span>
                  <input type="number" class="form-control form-control-sm sr-filter-input" placeholder="Max" v-model="filters.weightMax" min="0">
                </div>
                <div class="d-flex flex-wrap gap-1 mt-2">
                  <button class="btn btn-sm sr-price-chip" @click="filters.weightMin='0';filters.weightMax='50'">0–50kg</button>
                  <button class="btn btn-sm sr-price-chip" @click="filters.weightMin='50';filters.weightMax='150'">50–150kg</button>
                  <button class="btn btn-sm sr-price-chip" @click="filters.weightMin='150';filters.weightMax='300'">150–300kg</button>
                  <button class="btn btn-sm sr-price-chip" @click="filters.weightMin='300';filters.weightMax='500'">300–500kg</button>
                  <button class="btn btn-sm sr-price-chip" @click="filters.weightMin='500';filters.weightMax=''">500kg+</button>
                </div>
              </div>
            </div>

            <!-- Delivery -->
            <div class="sr-filter-group">
              <div class="sr-filter-title" @click="toggleFilter('delivery')">
                Delivery <i :class="openFilters.delivery ? 'mdi mdi-chevron-up' : 'mdi mdi-chevron-down'"></i>
              </div>
              <div v-if="openFilters.delivery" class="sr-filter-body">
                <label class="sr-filter-option">
                  <input type="checkbox" v-model="filters.hasDelivery"> <i class="mdi mdi-truck me-1" style="color:#50C878;"></i> Seller offers delivery
                </label>
              </div>
            </div>

            <!-- Verification -->
            <div class="sr-filter-group">
              <div class="sr-filter-title" @click="toggleFilter('verification')">
                Verification <i :class="openFilters.verification ? 'mdi mdi-chevron-up' : 'mdi mdi-chevron-down'"></i>
              </div>
              <div v-if="openFilters.verification" class="sr-filter-body">
                <label class="sr-filter-option">
                  <input type="checkbox" v-model="filters.verifiedOnly"> Verified sellers only
                </label>
                <label class="sr-filter-option">
                  <input type="checkbox" v-model="filters.featuredOnly"> Featured listings only
                </label>
              </div>
            </div>

            <!-- Performance Badge -->
            <div class="sr-filter-group">
              <div class="sr-filter-title" @click="toggleFilter('performance')">
                Performance <i :class="openFilters.performance ? 'mdi mdi-chevron-up' : 'mdi mdi-chevron-down'"></i>
              </div>
              <div v-if="openFilters.performance" class="sr-filter-body">
                <div style="color:var(--fm-s,#6B6356);font-size:11px;margin-bottom:6px;">Filter by the seller's growth track record on this animal.</div>
                <label class="sr-filter-option" v-for="b in performanceBadges" :key="'pb-'+b.value">
                  <input type="checkbox" :value="b.value" v-model="filters.performanceBadges" />
                  <span class="sr-badge-pill" :class="'sb-'+b.value"><i class="mdi" :class="b.icon"></i> {{ b.label }}</span>
                </label>
              </div>
            </div>

            <!-- Listing Type -->
            <div class="sr-filter-group">
              <div class="sr-filter-title" @click="toggleFilter('listingType')">
                Listing Type <i :class="openFilters.listingType ? 'mdi mdi-chevron-up' : 'mdi mdi-chevron-down'"></i>
              </div>
              <div v-if="openFilters.listingType" class="sr-filter-body">
                <label class="sr-filter-option">
                  <input type="radio" value="" v-model="filters.listingType"> All
                </label>
                <label class="sr-filter-option">
                  <input type="radio" value="individual" v-model="filters.listingType"> Individual
                </label>
                <label class="sr-filter-option">
                  <input type="radio" value="batch" v-model="filters.listingType"> Batch
                </label>
              </div>
            </div>

            <!-- Apply Button -->
            <button class="btn btn-warning text-dark fw-bold w-100 mt-3" @click="applyFilters">
              <i class="mdi mdi-magnify me-1"></i> Apply Filters
            </button>
          </div>
        </div>

        <!-- ===== RESULTS AREA ===== -->
        <div class="col-lg-9">

          <!-- Results Header -->
          <div class="sr-results-header">
            <div class="d-flex align-items-center flex-wrap" style="gap:8px;">
              <div class="sr-results-count" v-if="!loading">
                <strong>{{ totalResults }}</strong> listings found
                <span v-if="filters.keyword" style="color:#BFA164;"> for "{{ filters.keyword }}"</span>
              </div>
            </div>
            <div class="d-flex align-items-center flex-shrink-0" style="gap:12px;">
              <!-- Sort -->
              <select v-model="sortBy" class="form-select form-select-sm sr-sort-select" @change="applyFilters">
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="price_low">Price: Low to High</option>
                <option value="price_high">Price: High to Low</option>
                <option value="popular">Most Viewed</option>
                <option value="rating">Best Rated Seller</option>
                <option value="nearest" :disabled="!buyerLocation.lat">Nearest First</option>
              </select>
              <!-- Dev-Requests #5 — Near me proximity filter. Uses browser geolocation
                   and filters to configurable radius (default 100 km). -->
              <div class="d-flex flex-column align-items-end" style="gap:4px;">
                <div class="d-flex align-items-center" style="gap:6px;">
                  <button class="btn btn-sm" :class="proximityOn ? 'btn-warning' : 'btn-outline-warning'" @click="toggleProximity" style="font-size:12px;">
                    <i class="mdi mdi-map-marker-radius me-1"></i>
                    <span v-if="proximityOn">{{ radiusKm }} km</span>
                    <span v-else>Near me</span>
                  </button>
                  <select v-if="proximityOn" v-model.number="radiusKm" @change="applyFilters" class="form-select form-select-sm" style="width:auto;font-size:11px;">
                    <option :value="25">25 km</option>
                    <option :value="50">50 km</option>
                    <option :value="100">100 km</option>
                    <option :value="250">250 km</option>
                    <option :value="500">500 km</option>
                  </select>
                </div>
                <small v-if="geoError" style="color:#B78500;font-size:10px;max-width:220px;text-align:right;line-height:1.3;">
                  <i class="mdi mdi-map-marker-alert"></i> {{ geoError }}
                </small>
              </div>
              <!-- View Toggle -->
              <div class="sr-view-toggle">
                <button class="sr-view-btn" :class="{ active: viewMode === 'grid' }" @click="viewMode = 'grid'">
                  <i class="mdi mdi-view-grid"></i>
                </button>
                <button class="sr-view-btn" :class="{ active: viewMode === 'list' }" @click="viewMode = 'list'">
                  <i class="mdi mdi-view-list"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- Active Filter Chips -->
          <div class="d-flex flex-wrap mb-3" style="gap:6px;" v-if="activeFilterChips.length > 0">
            <span class="sr-active-chip" v-for="(chip, ci) in activeFilterChips" :key="ci">
              {{ chip.label }} <i class="mdi mdi-close" @click="removeChip(chip)"></i>
            </span>
            <button class="btn btn-link btn-sm text-muted p-0 ms-1" @click="clearAllFilters">Clear all</button>
          </div>

          <!-- Loading -->
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-warning" role="status"><span class="sr-only">Loading...</span></div>
            <p class="mt-2 text-muted">Searching listings...</p>
          </div>

          <!-- No Results -->
          <!-- Pilot-reviewer fix: zero-state used to be a dead-end. Now if the
               API returns suggestions (neighbour-county listings when keyword
               matches a known county, or freshest 6 otherwise), we render
               them right under the "no results" message in the same grid. -->
          <div v-else-if="listings.length === 0" class="text-center py-5">
            <i class="mdi mdi-magnify" style="font-size:64px;color:var(--fm-s,#6B6356);"></i>
            <h5 class="mt-3" style="color:var(--fm-h,#F5F0E8);">
              No results<span v-if="filters.keyword"> for "{{ filters.keyword }}"</span>
            </h5>
            <p class="text-muted">Try adjusting your filters or search terms</p>
            <button class="btn btn-outline-warning" @click="clearAllFilters">Clear All Filters</button>

            <div v-if="suggestions.length > 0" class="mt-5 text-start">
              <div class="sr-suggestions-header mb-3">
                <h6 style="color:var(--fm-h,#F5F0E8);margin-bottom:4px;">
                  <i class="mdi mdi-lightbulb-outline me-1" style="color:#BFA164;"></i>
                  You might like
                </h6>
                <div style="color:var(--fm-s,#6B6356);font-size:12px;">{{ suggestionsReason }}</div>
              </div>
              <div class="row g-3">
                <div class="col-lg-4 col-md-6 col-6" v-for="(item, index) in suggestions" :key="'sg-'+index">
                  <div class="sr-card-grid" @click="$router.push('/marketplace/listing/' + item.id)">
                    <div class="sr-card-img">
                      <img v-if="item.primary_photo" :src="photoUrl(item.primary_photo)" :alt="item.title" class="sr-card-photo" />
                      <div v-else class="sr-card-placeholder">
                        <i :class="getSpeciesIcon(item.species_name)" style="font-size:48px;color:rgba(191,161,100,0.3);"></i>
                      </div>
                    </div>
                    <div class="sr-card-body">
                      <h6 class="sr-card-title">{{ item.title }}</h6>
                      <div class="sr-card-location"><i class="mdi mdi-map-marker"></i> {{ item.county_name || item.location }}<span v-if="item.distance_km !== undefined && item.distance_km !== null" style="color:#BFA164;font-weight:600;"> · {{ Number(item.distance_km).toFixed(0) }} km</span></div>
                      <div class="sr-card-footer">
                        <div class="sr-card-price">
                          {{ (item.currency || 'KES') }} {{ formatPrice(item.price) }}
                          <span class="sr-card-unit">{{ perKgSub(item) || priceUnitLabel(item.price_unit) }}</span>
                        </div>
                        <div class="sr-card-seller">
                          <div class="sr-seller-avatar">{{ getInitials(item.seller_name) }}</div>
                          <span>{{ item.seller_name }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- GRID VIEW -->
          <div v-else-if="viewMode === 'grid'" class="row g-3">
            <div class="col-lg-4 col-md-6 col-6" v-for="(item, index) in listings" :key="'g-'+index">
              <div class="sr-card-grid" :class="{'sr-card-sold': isSoldShowcase(item)}" @click="$router.push('/marketplace/listing/' + item.id)">
                <div class="sr-card-img">
                  <img v-if="item.primary_photo" :src="photoUrl(item.primary_photo)" :alt="item.title" class="sr-card-photo" />
                  <div v-else class="sr-card-placeholder">
                    <i :class="getSpeciesIcon(item.species_name)" style="font-size:48px;color:rgba(191,161,100,0.3);"></i>
                  </div>
                  <!-- Diagonal SOLD watermark across the photo (sold-showcase only). -->
                  <div v-if="isSoldShowcase(item)" class="sr-sold-overlay">
                    <div class="sr-sold-stamp">SOLD</div>
                  </div>
                  <div class="sr-card-badges">
                    <span v-if="item.seller_verified" class="badge bg-success bg-opacity-25 text-success" style="font-size:10px;" title="Seller has completed KYC identity verification"><i class="mdi mdi-check-decagram"></i> Seller Verified</span><span v-if="Number(item.vet_verified) === 1" class="badge bg-info bg-opacity-25 text-info" style="font-size:10px;" title="Animal inspected and verified by a vet on Flockr"><i class="mdi mdi-stethoscope"></i> Vet Verified</span><span v-if="item.anitrac_id && item.anitrac_verified_at" class="badge" style="background:rgba(80,200,120,.18);color:#50C878;font-weight:600;" :title="'ANITRAC: ' + item.anitrac_id + ' (verified)'"><i class="mdi mdi-shield-check me-1"></i>ANITRAC ✓</span><span v-else-if="item.anitrac_id" class="badge" style="background:rgba(160,152,136,.18);color:#A09888;font-weight:600;" :title="'ANITRAC: ' + item.anitrac_id + ' (pending verification)'"><i class="mdi mdi-shield-outline me-1"></i>ANITRAC pending</span>
                    <span v-if="item.is_featured" class="badge bg-warning bg-opacity-25 text-warning" style="font-size:10px;"><i class="mdi mdi-star"></i> Featured</span>
                    <span v-if="item.performance_badge && item.performance_badge !== 'no_data'" class="sr-badge-pill" :class="'sb-'+item.performance_badge" :title="'Seller growth track record on this animal'">
                      <i class="mdi" :class="perfBadgeIcon(item.performance_badge)"></i>{{ perfBadgeLabel(item.performance_badge) }}
                    </span>
                  </div>
                  <button v-if="!isSoldShowcase(item)" class="sr-save-btn" @click.stop="toggleSave(item)">
                    <i :class="item.is_saved ? 'mdi mdi-heart text-danger' : 'mdi mdi-heart-outline'"></i>
                  </button>
                </div>
                <div class="sr-card-body">
                  <h6 class="sr-card-title">{{ item.title }}</h6>
                  <div class="sr-card-meta">
                    <span v-if="item.weight"><i class="mdi mdi-weight-kilogram"></i> {{ item.weight }} kg</span>
                    <span v-if="item.sex"><i class="mdi mdi-gender-male-female"></i> {{ item.sex }}</span>
                    <span v-if="item.age_text"><i class="mdi mdi-clock-outline"></i> {{ item.age_text }}</span>
                  </div>
                  <div class="sr-card-location"><i class="mdi mdi-map-marker"></i> {{ item.county_name || item.location }}<span v-if="item.distance_km !== undefined && item.distance_km !== null" style="color:#BFA164;font-weight:600;"> · {{ Number(item.distance_km).toFixed(0) }} km</span></div>
                  <div class="sr-card-footer">
                    <div class="sr-card-price">
                      {{ (item.currency || 'KES') }} {{ formatPrice(item.price) }}
                      <span class="sr-card-unit">{{ perKgSub(item) || priceUnitLabel(item.price_unit) }}</span>
                    </div>
                    <div class="sr-card-seller">
                      <div class="sr-seller-avatar">{{ getInitials(item.seller_name) }}</div>
                      <span>{{ item.seller_name }}</span>
                      <i class="mdi mdi-star text-warning" v-if="item.seller_rating"></i>
                      <span v-if="item.seller_rating">{{ item.seller_rating }}</span>
                    </div>
                  </div>
                  <!-- Sold-to caption + (seller-only) hide-from-showcase link. -->
                  <div v-if="isSoldShowcase(item)" class="sr-sold-meta">
                    <div class="sr-sold-caption">Sold to {{ item.sold_to_buyer_name || 'a buyer' }}</div>
                    <a v-if="isOwnListing(item)" href="#" class="sr-sold-hide" @click.stop.prevent="hideFromShowcase(item)">
                      <i class="mdi mdi-eye-off-outline"></i> Hide from showcase
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- LIST VIEW -->
          <div v-else class="d-flex flex-column" style="gap:12px;">
            <div class="sr-card-list" :class="{'sr-card-sold': isSoldShowcase(item)}" v-for="(item, index) in listings" :key="'l-'+index"
                 @click="$router.push('/marketplace/listing/' + item.id)">
              <div class="sr-list-img">
                <img v-if="item.primary_photo" :src="photoUrl(item.primary_photo)" :alt="item.title" class="sr-list-photo" />
                <div v-else class="sr-list-placeholder">
                  <i :class="getSpeciesIcon(item.species_name)" style="font-size:36px;color:rgba(191,161,100,0.3);"></i>
                </div>
                <!-- Diagonal SOLD watermark across the photo (sold-showcase only). -->
                <div v-if="isSoldShowcase(item)" class="sr-sold-overlay sr-sold-overlay-list">
                  <div class="sr-sold-stamp sr-sold-stamp-list">SOLD</div>
                </div>
              </div>
              <div class="sr-list-body">
                <div class="d-flex justify-content-between align-items-start">
                  <div>
                    <h6 class="sr-card-title mb-1">{{ item.title }}</h6>
                    <div class="sr-card-meta mb-1">
                      <span v-if="item.weight"><i class="mdi mdi-weight-kilogram"></i> {{ item.weight }} kg</span>
                      <span v-if="item.sex"><i class="mdi mdi-gender-male-female"></i> {{ item.sex }}</span>
                      <span v-if="item.age_text"><i class="mdi mdi-clock-outline"></i> {{ item.age_text }}</span>
                      <span v-if="item.quantity"><i class="mdi mdi-numeric"></i> Qty: {{ item.quantity }}</span>
                    </div>
                    <div class="sr-card-location mb-1"><i class="mdi mdi-map-marker"></i> {{ item.county_name || item.location }}<span v-if="item.distance_km !== undefined && item.distance_km !== null" style="color:#BFA164;font-weight:600;"> · {{ Number(item.distance_km).toFixed(0) }} km</span></div>
                    <div class="d-flex align-items-center" style="gap:6px;">
                      <span v-if="item.seller_verified" class="badge bg-success bg-opacity-25 text-success" style="font-size:10px;" title="Seller has completed KYC identity verification"><i class="mdi mdi-check-decagram"></i> Seller Verified</span><span v-if="Number(item.vet_verified) === 1" class="badge bg-info bg-opacity-25 text-info" style="font-size:10px;" title="Animal inspected and verified by a vet on Flockr"><i class="mdi mdi-stethoscope"></i> Vet Verified</span><span v-if="item.anitrac_id && item.anitrac_verified_at" class="badge" style="background:rgba(80,200,120,.18);color:#50C878;font-weight:600;" :title="'ANITRAC: ' + item.anitrac_id + ' (verified)'"><i class="mdi mdi-shield-check me-1"></i>ANITRAC ✓</span><span v-else-if="item.anitrac_id" class="badge" style="background:rgba(160,152,136,.18);color:#A09888;font-weight:600;" :title="'ANITRAC: ' + item.anitrac_id + ' (pending verification)'"><i class="mdi mdi-shield-outline me-1"></i>ANITRAC pending</span>
                      <span v-if="item.is_featured" class="badge bg-warning bg-opacity-25 text-warning" style="font-size:10px;"><i class="mdi mdi-star"></i> Featured</span>
                    </div>
                    <!-- Sold-to caption + seller-only hide link. -->
                    <div v-if="isSoldShowcase(item)" class="sr-sold-meta mt-1">
                      <div class="sr-sold-caption">Sold to {{ item.sold_to_buyer_name || 'a buyer' }}</div>
                      <a v-if="isOwnListing(item)" href="#" class="sr-sold-hide" @click.stop.prevent="hideFromShowcase(item)">
                        <i class="mdi mdi-eye-off-outline"></i> Hide from showcase
                      </a>
                    </div>
                  </div>
                  <div class="text-end">
                    <div class="sr-card-price mb-1">
                      {{ (item.currency || 'KES') }} {{ formatPrice(item.price) }}
                      <span class="sr-card-unit">{{ perKgSub(item) || priceUnitLabel(item.price_unit) }}</span>
                    </div>
                    <div class="sr-card-seller">
                      <div class="sr-seller-avatar">{{ getInitials(item.seller_name) }}</div>
                      <span>{{ item.seller_name }}</span>
                      <i class="mdi mdi-star text-warning" v-if="item.seller_rating"></i>
                      <span v-if="item.seller_rating">{{ item.seller_rating }}</span>
                    </div>
                    <button v-if="!isSoldShowcase(item)" class="btn btn-sm btn-outline-warning mt-2" @click.stop="toggleSave(item)">
                      <i :class="item.is_saved ? 'mdi mdi-heart text-danger' : 'mdi mdi-heart-outline me-1'"></i>
                      {{ item.is_saved ? 'Saved' : 'Save' }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Pagination -->
          <div class="sr-pagination" v-if="totalPages > 1 && !loading">
            <button class="sr-page-btn" :disabled="currentPage <= 1" @click="goToPage(currentPage - 1)">
              <i class="mdi mdi-chevron-left"></i>
            </button>
            <template v-for="p in paginationRange" :key="'p-'+p">
              <button v-if="p === '...'" class="sr-page-btn" disabled>...</button>
              <button v-else class="sr-page-btn" :class="{ active: currentPage === p }" @click="goToPage(p)">{{ p }}</button>
            </template>
            <button class="sr-page-btn" :disabled="currentPage >= totalPages" @click="goToPage(currentPage + 1)">
              <i class="mdi mdi-chevron-right"></i>
            </button>
            <span class="text-muted ms-2" style="font-size:13px;">Page {{ currentPage }} of {{ totalPages }}</span>
          </div>

        </div>
      </div>
    </div>

  </div>
  <div class="rightbar-overlay"></div>
  </div>
  </div>
</template>

<script>
import { executeJson, flocker, uploads } from "@/api";

export default {
  name: "ListingSearchResults",

  data() {
    return {
      darkMode:(document.documentElement.getAttribute('data-theme')||localStorage.getItem('flockr_theme')||'dark')!=='light',

      // Current viewer — used to surface the "Hide from showcase" link on the
      // seller's own sold listings (compared against listing.seller_id).
      currentUserId: sessionStorage.getItem('userId') || '',

      // Results
      listings: [],
      // Pilot-reviewer fix: zero-result suggestions surfaced by API
      suggestions: [],
      suggestionsReason: '',
      totalResults: 0,
      totalPages: 0,
      currentPage: 1,
      perPage: 12,
      loading: false,

      // View
      viewMode: 'grid',
      sortBy: 'newest',
      showFilters: false,

      // Dev-Requests #5 — proximity filter. buyerLocation is populated from browser
      // geolocation. radiusKm defaults to 100 (roughly a county diameter in Kenya).
      buyerLocation: { lat: null, lng: null },
      proximityOn: false,
      radiusKm: 100,
      geoError: '',

      // Filter panel open states
      openFilters: {
        kind: true,
        productCategory: true,
        species: true,
        breed: false,
        county: true,
        price: true,
        sex: false,
        age: false,
        weight: false,
        delivery: false,
        verification: false,
        listingType: false,
        performance: false
      },
      productCategories: [],

      // V2.b — badge filter options
      performanceBadges: [
        { value: 'star',         label: 'Star Performer', icon: 'mdi-star' },
        { value: 'on_track',     label: 'On Track',       icon: 'mdi-check-circle' },
        { value: 'watch',        label: 'Watch',          icon: 'mdi-eye-outline' },
        { value: 'at_risk',      label: 'At Risk',        icon: 'mdi-alert' },
        { value: 'establishing', label: 'Establishing',   icon: 'mdi-progress-clock' }
      ],

      // Filter values
      filters: {
        keyword: '',
        listingKind: '',           // '' | live_animal | livestock_product
        productCategoryIds: [],    // when livestock_product
        species: [],
        breeds: [],
        counties: [],
        priceMin: '',
        priceMax: '',
        sex: '',
        verifiedOnly: false,
        featuredOnly: false,
        listingType: '',
        hasDelivery: false,
        ageMin: '',
        ageMax: '',
        weightMin: '',
        weightMax: '',
        performanceBadges: []
      },

      // Filter data from API
      speciesList: [],
      breedsList: [],
      countiesList: [],
      countySearch: '',

      // Loading states
      loadingSpecies: false,
      loadingBreeds: false
    };
  },

  computed: {
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#F7F5F2;min-height:100vh;color:#1A1A1A;'},
    themeCardStyle(){return this.darkMode?'background:#141414!important;border:1px solid #1E1E1E!important;':'background:#FFFFFF!important;border:1px solid #E8E2DA!important;'},
    themeInputStyle(){return this.darkMode?'background:#0F0F0F;border:1px solid #2A2520;color:#F5F0E8;':'background:#FFFFFF;border:1px solid #E0D8D0;color:#1A1A1A;'},

    filteredCounties() {
      if (!this.countySearch) return this.countiesList;
      var search = this.countySearch.toLowerCase();
      return this.countiesList.filter(function(c) {
        return (c.county_name || c.name || '').toLowerCase().includes(search);
      });
    },

    activeFilterCount() {
      var count = 0;
      if (this.filters.keyword) count++;
      count += this.filters.species.length;
      count += this.filters.breeds.length;
      count += this.filters.counties.length;
      if (this.filters.priceMin || this.filters.priceMax) count++;
      if (this.filters.sex) count++;
      if (this.filters.ageMin || this.filters.ageMax) count++;
      if (this.filters.weightMin || this.filters.weightMax) count++;
      if (this.filters.hasDelivery) count++;
      if (this.filters.verifiedOnly) count++;
      if (this.filters.featuredOnly) count++;
      if (this.filters.listingType) count++;
      return count;
    },

    activeFilterChips() {
      var chips = [];
      var self = this;

      if (this.filters.keyword) {
        chips.push({ type: 'keyword', label: '"' + this.filters.keyword + '"' });
      }

      this.filters.species.forEach(function(spId) {
        var sp = self.speciesList.find(function(s) { return s.id == spId; });
        if (sp) chips.push({ type: 'species', id: spId, label: sp.name });
      });

      this.filters.breeds.forEach(function(brId) {
        var br = self.breedsList.find(function(b) { return b.id == brId; });
        if (br) chips.push({ type: 'breed', id: brId, label: br.name });
      });

      this.filters.counties.forEach(function(cId) {
        var c = self.countiesList.find(function(co) { return co.id == cId; });
        if (c) chips.push({ type: 'county', id: cId, label: c.county_name || c.name });
      });

      if (this.filters.priceMin || this.filters.priceMax) {
        var label = 'KES ' + (this.filters.priceMin || '0') + ' - ' + (this.filters.priceMax || '∞');
        chips.push({ type: 'price', label: label });
      }

      if (this.filters.sex) {
        chips.push({ type: 'sex', label: this.filters.sex.charAt(0).toUpperCase() + this.filters.sex.slice(1) });
      }

      if (this.filters.verifiedOnly) chips.push({ type: 'verified', label: 'Verified Only' });
      if (this.filters.featuredOnly) chips.push({ type: 'featured', label: 'Featured Only' });
      if (this.filters.listingType) {
        chips.push({ type: 'listingType', label: this.filters.listingType.charAt(0).toUpperCase() + this.filters.listingType.slice(1) });
      }

      if (this.filters.ageMin || this.filters.ageMax) {
        chips.push({ type: 'age', label: 'Age: ' + (this.filters.ageMin || '0') + '–' + (this.filters.ageMax || '∞') + 'mo' });
      }
      if (this.filters.weightMin || this.filters.weightMax) {
        chips.push({ type: 'weight', label: 'Weight: ' + (this.filters.weightMin || '0') + '–' + (this.filters.weightMax || '∞') + 'kg' });
      }
      if (this.filters.hasDelivery) chips.push({ type: 'delivery', label: 'Offers Delivery' });

      return chips;
    },

    paginationRange() {
      var pages = [];
      var total = this.totalPages;
      var current = this.currentPage;

      if (total <= 7) {
        for (var i = 1; i <= total; i++) pages.push(i);
      } else {
        pages.push(1);
        if (current > 3) pages.push('...');
        var start = Math.max(2, current - 1);
        var end = Math.min(total - 1, current + 1);
        for (var j = start; j <= end; j++) pages.push(j);
        if (current < total - 2) pages.push('...');
        pages.push(total);
      }
      return pages;
    }
  },

  watch: {
    'filters.species': function() {
      this.fetchBreeds();
    }
  },

  mounted() {
    // Apply global theme
    var t=localStorage.getItem('flockr_theme')||'dark';window.__flockrSetTheme(t);

    this.parseQueryParams();
    this.fetchFilterData();
    this.fetchListings();
  },

  methods: {
    perfBadgeLabel(b){ return ({star:'Star',on_track:'On Track',watch:'Watch',at_risk:'At Risk',establishing:'New'})[b] || b; },
    perfBadgeIcon(b){ return ({star:'mdi-star',on_track:'mdi-check-circle',watch:'mdi-eye-outline',at_risk:'mdi-alert',establishing:'mdi-progress-clock'})[b] || 'mdi-help-circle'; },
    photoUrl(p){if(!p)return'';if(p.startsWith&&p.startsWith('http'))return p;return uploads+String(p).replace('uploads/','')},

    // Sold-showcase helpers. The API returns is_sold_showcase as a string '0'|'1'
    // (mysqli returns ints as strings); coerce both sides for safety.
    isSoldShowcase(item){ return String(item.is_sold_showcase) === '1'; },
    isOwnListing(item){ return this.currentUserId && String(item.seller_id) === String(this.currentUserId); },

    async hideFromShowcase(item) {
      if (!this.currentUserId) { this.$router.push('/login'); return; }
      if (!confirm('Hide this sold listing from the marketplace showcase?')) return;
      try {
        var response = await executeJson({
          function: 'hideSoldListing',
          userId: this.currentUserId,
          listingId: item.id
        }, flocker);
        if (response.data && response.data.success) {
          // Drop the card from the local list — no full refetch needed.
          this.listings = this.listings.filter(function(l){ return l.id !== item.id; });
        } else {
          alert((response.data && response.data.message) || 'Failed to hide listing');
        }
      } catch (e) {
        console.error('Error hiding sold listing:', e);
        alert('Network error');
      }
    },
    

    // ============ INITIALIZATION ============

    parseQueryParams() {
      var q = this.$route.query;
      if (q.keyword) this.filters.keyword = q.keyword;
      if (q.species) this.filters.species = Array.isArray(q.species) ? q.species : [q.species];
      if (q.speciesId) this.filters.species = Array.isArray(q.speciesId) ? q.speciesId : [q.speciesId];
      if (q.breeds) this.filters.breeds = Array.isArray(q.breeds) ? q.breeds : [q.breeds];
      if (q.county) this.filters.counties = Array.isArray(q.county) ? q.county : [q.county];
      if (q.price_min) this.filters.priceMin = q.price_min;
      if (q.price_max) this.filters.priceMax = q.price_max;
      if (q.sex) this.filters.sex = q.sex;
      if (q.age_min) this.filters.ageMin = q.age_min;
      if (q.age_max) this.filters.ageMax = q.age_max;
      if (q.weight_min) this.filters.weightMin = q.weight_min;
      if (q.weight_max) this.filters.weightMax = q.weight_max;
      if (q.delivery === '1') this.filters.hasDelivery = true;
      if (q.verified === '1') this.filters.verifiedOnly = true;
      if (q.featured === '1') this.filters.featuredOnly = true;
      if (q.sort) this.sortBy = q.sort;
      if (q.page) this.currentPage = parseInt(q.page) || 1;
      if (q.view) this.viewMode = q.view;
      if (q.type) this.filters.listingType = q.type;
    },

    async fetchFilterData() {
      this.fetchSpecies();
      this.fetchCounties();
    },

    // ============ API CALLS ============

    async fetchSpecies() {
      this.loadingSpecies = true;
      try {
        var response = await executeJson({ function: "getAnimalSpecies" }, flocker);
        if (response.data.success) {
          this.speciesList = response.data.data.species || [];
        }
      } catch (e) {
        console.error('Error fetching species:', e);
      } finally {
        this.loadingSpecies = false;
      }
    },

    async fetchBreeds() {
      if (this.filters.species.length === 0) {
        this.breedsList = [];
        return;
      }
      this.loadingBreeds = true;
      try {
        var response = await executeJson({
          function: "getAnimalBreeds",
          speciesId: this.filters.species[0]
        }, flocker);
        if (response.data.success) {
          this.breedsList = response.data.data.breeds || [];
        }
      } catch (e) {
        console.error('Error fetching breeds:', e);
      } finally {
        this.loadingBreeds = false;
      }
    },

    async fetchCounties() {
      try {
        var response = await executeJson({ function: "getCounties", countryCode: "KE" }, flocker);
        if (response.data.success) {
          this.countiesList = response.data.data.counties || [];
        }
      } catch (e) {
        console.error('Error fetching counties:', e);
      }
    },

    async fetchListings() {
      this.loading = true;
      try {
        var params = {
          function: "searchListings",
          limit: this.perPage,
          page: this.currentPage,
          sortBy: this.sortBy,
          includeSold: '1'
        };

        if (this.filters.keyword) params.keyword = this.filters.keyword;
        if (this.filters.species.length > 0) params.speciesIds = this.filters.species.join(',');
        if (this.filters.breeds.length > 0) params.breedIds = this.filters.breeds.join(',');
        if (this.filters.counties.length > 0) params.countyIds = this.filters.counties.join(',');
        if (this.filters.priceMin) params.priceMin = this.filters.priceMin;
        if (this.filters.priceMax) params.priceMax = this.filters.priceMax;
        if (this.filters.sex) params.sex = this.filters.sex;
        if (this.filters.listingType) params.listingType = this.filters.listingType;
        if (this.filters.verifiedOnly) params.verifiedOnly = '1';
        if (this.filters.featuredOnly) params.featuredOnly = '1';
        if (this.filters.hasDelivery) params.hasDelivery = '1';
        if (this.filters.performanceBadges && this.filters.performanceBadges.length) {
          params.performanceBadge = this.filters.performanceBadges.join(',');
        }
        if (this.filters.ageMin) params.ageMin = this.filters.ageMin;
        if (this.filters.ageMax) params.ageMax = this.filters.ageMax;
        if (this.filters.weightMin) params.weightMin = this.filters.weightMin;
        if (this.filters.weightMax) params.weightMax = this.filters.weightMax;

        // Dev-Requests #4 — live animals vs livestock products (+ category)
        if (this.filters.listingKind) params.listingKind = this.filters.listingKind;
        if (this.filters.productCategoryIds && this.filters.productCategoryIds.length) {
          params.productCategoryIds = this.filters.productCategoryIds.join(',');
        }

        // Dev-Requests #5 — proximity params
        if (this.proximityOn && this.buyerLocation.lat && this.buyerLocation.lng) {
          params.latitude  = this.buyerLocation.lat;
          params.longitude = this.buyerLocation.lng;
          params.radiusKm  = this.radiusKm;
        }

        var response = await executeJson(params, flocker);
        if (response.data.success) {
          this.listings = response.data.data.listings || [];
          this.totalResults = response.data.data.total || 0;
          this.totalPages = Math.ceil(this.totalResults / this.perPage);
          // Pilot-reviewer fix: API now ships nearby suggestions on zero-result
          // queries (e.g. "Nairobi" → Kiambu/Kajiado/Machakos), so users never
          // dead-end on an empty page.
          this.suggestions = response.data.data.suggestions || [];
          this.suggestionsReason = response.data.data.suggestionsReason || '';
        }
      } catch (e) {
        console.error('Error fetching listings:', e);
      } finally {
        this.loading = false;
      }
    },

    async toggleSave(item) {
      var userId = sessionStorage.getItem('userId');
      if (!userId) {
        this.$router.push('/login');
        return;
      }
      try {
        var response = await executeJson({
          function: "saveListing",
          userId: userId,
          listingId: item.id
        }, flocker);
        if (response.data.success) {
          item.is_saved = !item.is_saved;
        }
      } catch (e) {
        console.error('Error toggling save:', e);
      }
    },

    // ============ FILTER ACTIONS ============

    // Dev-Requests #4 — lazy-load livestock product categories on demand.
    async onKindChange() {
      this.filters.productCategoryIds = [];
      if (this.filters.listingKind === 'livestock_product' && !this.productCategories.length) {
        try {
          const r = await executeJson({ function: 'getLivestockProductCategories' }, flocker);
          if (r.data && r.data.success) this.productCategories = r.data.data.categories || [];
        } catch(e) { console.error(e); }
      }
      this.applyFilters();
    },

    // Dev-Requests #5 — enable/disable the proximity filter. Grabs the buyer's
    // location on first enable; caches it for the rest of the session. Falls back
    // gracefully when the browser blocks getCurrentPosition (insecure origin, etc.).
    toggleProximity() {
      if (this.proximityOn) {
        this.proximityOn = false;
        this.applyFilters();
        return;
      }
      if (this.buyerLocation.lat && this.buyerLocation.lng) {
        this.proximityOn = true;
        this.applyFilters();
        return;
      }
      if (!navigator.geolocation) {
        this.geoError = 'Your browser does not support location. Pick a county from the filter panel instead.';
        return;
      }
      var isSecure = window.isSecureContext || location.protocol === 'https:' || location.hostname === 'localhost' || location.hostname === '127.0.0.1';
      if (!isSecure) {
        this.geoError = 'Location needs an HTTPS site. Pick a county from the filter panel instead.';
        return;
      }
      var self = this;
      this.geoError = '';
      navigator.geolocation.getCurrentPosition(function(pos){
        self.buyerLocation = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        self.proximityOn = true;
        self.applyFilters();
      }, function(err){
        var reason = err && err.code === 1 ? 'you blocked location access' :
                     err && err.code === 2 ? 'position unavailable' :
                     err && err.code === 3 ? 'took too long — check your network' :
                     (err && err.message) || 'unknown error';
        self.geoError = 'Could not get your location (' + reason + '). Pick a county from the filter panel instead.';
      }, { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 });
    },

    applyFilters() {
      this.currentPage = 1;
      this.updateURL();
      this.fetchListings();
      this.showFilters = false;
    },

    clearAllFilters() {
      this.filters = {
        keyword: '',
        species: [],
        breeds: [],
        counties: [],
        priceMin: '',
        priceMax: '',
        sex: '',
        verifiedOnly: false,
        featuredOnly: false,
        listingType: '',
        hasDelivery: false,
        ageMin: '',
        ageMax: '',
        weightMin: '',
        weightMax: ''
      };
      this.sortBy = 'newest';
      this.applyFilters();
    },

    removeChip(chip) {
      if (chip.type === 'keyword') this.filters.keyword = '';
      if (chip.type === 'species') this.filters.species = this.filters.species.filter(function(id) { return id != chip.id; });
      if (chip.type === 'breed') this.filters.breeds = this.filters.breeds.filter(function(id) { return id != chip.id; });
      if (chip.type === 'county') this.filters.counties = this.filters.counties.filter(function(id) { return id != chip.id; });
      if (chip.type === 'price') { this.filters.priceMin = ''; this.filters.priceMax = ''; }
      if (chip.type === 'sex') this.filters.sex = '';
      if (chip.type === 'age') { this.filters.ageMin = ''; this.filters.ageMax = ''; }
      if (chip.type === 'weight') { this.filters.weightMin = ''; this.filters.weightMax = ''; }
      if (chip.type === 'delivery') this.filters.hasDelivery = false;
      if (chip.type === 'verified') this.filters.verifiedOnly = false;
      if (chip.type === 'featured') this.filters.featuredOnly = false;
      if (chip.type === 'listingType') this.filters.listingType = '';
      this.applyFilters();
    },

    setPriceRange(min, max) {
      this.filters.priceMin = min || '';
      this.filters.priceMax = max || '';
    },

    toggleFilter(name) {
      this.openFilters[name] = !this.openFilters[name];
    },

    goToPage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
      this.updateURL();
      this.fetchListings();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },

    updateURL() {
      var query = {};
      if (this.filters.keyword) query.keyword = this.filters.keyword;
      if (this.filters.species.length > 0) query.species = this.filters.species;
      if (this.filters.breeds.length > 0) query.breeds = this.filters.breeds;
      if (this.filters.counties.length > 0) query.county = this.filters.counties;
      if (this.filters.priceMin) query.price_min = this.filters.priceMin;
      if (this.filters.priceMax) query.price_max = this.filters.priceMax;
      if (this.filters.sex) query.sex = this.filters.sex;
      if (this.filters.ageMin) query.age_min = this.filters.ageMin;
      if (this.filters.ageMax) query.age_max = this.filters.ageMax;
      if (this.filters.weightMin) query.weight_min = this.filters.weightMin;
      if (this.filters.weightMax) query.weight_max = this.filters.weightMax;
      if (this.filters.hasDelivery) query.delivery = '1';
      if (this.filters.verifiedOnly) query.verified = '1';
      if (this.filters.featuredOnly) query.featured = '1';
      if (this.sortBy !== 'newest') query.sort = this.sortBy;
      if (this.currentPage > 1) query.page = this.currentPage;
      if (this.viewMode !== 'grid') query.view = this.viewMode;
      if (this.filters.listingType) query.type = this.filters.listingType;

      this.$router.replace({ path: '/marketplace/search', query: query }).catch(function() {});
    },

    // ============ HELPERS ============

    formatPrice(val) {
      if (!val) return '0';
      return Number(val).toLocaleString('en-KE');
    },

    // Pilot-reviewer fix (issue #2): show the unit alongside the price so a
    // 280 KES/kg-live listing reads differently than a 280 KES/head listing.
    priceUnitLabel(u) {
      switch (u) {
        case 'per_kg_live':    return '/ kg live';
        case 'per_kg_dressed': return '/ kg dressed';
        case 'per_batch':      return '/ batch';
        case 'per_liter':
        case 'per_litre':      return '/ litre';
        case 'per_head':       return '/ head';
        // product units (per_piece, per_tray, per_kg, per_dozen, ...)
        default:               return u ? '/ ' + String(u).replace(/^per_/, '').replace(/_/g, ' ') : '';
      }
    },

    // Issue #82 — per-kg listings store the computed TOTAL in price, so show
    // the math (rate/kg × weight) instead of a misleading "/kg" suffix.
    perKgSub(item) {
      if (String((item && item.price_unit) || '').indexOf('per_kg') !== 0) return '';
      var w = Number(item.weight_kg) || 0, p = Number(item.price) || 0;
      if (w <= 0 || p <= 0) return '';
      var rate = Math.round((p / w) * 100) / 100;
      return '(' + this.formatPrice(rate) + '/kg × ' + w + ' kg)';
    },

    getInitials(name) {
      if (!name) return '?';
      var parts = name.trim().split(' ');
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
      return name.slice(0, 2).toUpperCase();
    },

    getSpeciesIcon(speciesName) {
      if (!speciesName) return 'mdi mdi-paw';
      var s = speciesName.toLowerCase();
      if (s.includes('cattle') || s.includes('cow') || s.includes('bull') || s.includes('heifer')) return 'mdi mdi-cow';
      if (s.includes('goat')) return 'mdi mdi-alpha-g-circle';
      if (s.includes('sheep') || s.includes('ram') || s.includes('ewe') || s.includes('dorper')) return 'mdi mdi-sheep';
      if (s.includes('poultry') || s.includes('chicken') || s.includes('kienyeji') || s.includes('bird')) return 'mdi mdi-bird';
      if (s.includes('pig') || s.includes('sow') || s.includes('boar')) return 'mdi mdi-pig';
      if (s.includes('camel')) return 'mdi mdi-alpha-c-circle';
      return 'mdi mdi-paw';
    }
  }
};
</script>

<style>

/* === GLOBAL THEME === */
html[data-theme="light"] body { background:#FFFFFF!important; color:#1A1A1A!important; }
html[data-theme="light"] .fm-card,html[data-theme="light"] .p2c,html[data-theme="light"] .ld-section,html[data-theme="light"] .ld-action-card,html[data-theme="light"] .ld-seller-card,html[data-theme="light"] .ld-info-card,html[data-theme="light"] .ld-related-card { background:#FFFFFF!important; border-color:#E8E2DA!important; }
html[data-theme="light"] .fm-overlay .fm-modal,html[data-theme="light"] .ad-overlay .ad-modal { background:#FFFFFF!important; border-color:#E8E2DA!important; }
html[data-theme="light"] .fm-input,html[data-theme="light"] .fm-select { background:#F7F5F2!important; border-color:#E0D8D0!important; color:#1A1A1A!important; }
html[data-theme="light"] .fm-label { color:#555!important; }
html[data-theme="dark"] body { background:#0A0A0A!important; color:#F5F0E8!important; }
</style>
<style>
:root{--fm-h:#F5F0E8;--fm-s:#6B6356;--fm-m:#A09888;--fm-bg1:#141414;--fm-bg2:#0F0F0F;--fm-bg3:#1E1E1E;--fm-br:#1E1E1E}
html[data-theme="light"]{--fm-h:#1A1A1A;--fm-s:#777;--fm-m:#555;--fm-bg1:#FFFFFF;--fm-bg2:#F7F5F2;--fm-bg3:#F0EBE4;--fm-br:#E8E2DA}
html[data-theme="light"] body{background:#F7F5F2!important;color:#1A1A1A!important}
html[data-theme="light"] .card{background:#FFFFFF!important;border-color:#E8E2DA!important}
html[data-theme="light"] .card-body{color:#1A1A1A!important}
html[data-theme="light"] .modal-content{background:#FFFFFF!important;border-color:#E8E2DA!important;color:#1A1A1A!important}
html[data-theme="light"] .form-control,html[data-theme="light"] .form-select{background:#FFFFFF!important;border-color:#E0D8D0!important;color:#1A1A1A!important}
html[data-theme="light"] .table{color:#1A1A1A!important}
html[data-theme="light"] .table th{color:#555!important;border-color:#E8E2DA!important}
html[data-theme="light"] .table td{border-color:#F0EBE4!important}
html[data-theme="light"] [style*="color:#F5F0E8"]{color:#1A1A1A!important}
html[data-theme="light"] [style*="color:#6B6356"]{color:#777!important}
html[data-theme="light"] [style*="color:#A09888"]{color:#555!important}
html[data-theme="light"] [style*="background:#0F0F0F"]{background:#F7F5F2!important}
html[data-theme="light"] [style*="background:#141414"]{background:#FFFFFF!important}
html[data-theme="light"] [style*="background:#1E1E1E"]{background:#F0EBE4!important}
html[data-theme="light"] [style*="background:#0A0A0A"]{background:#F7F5F2!important}
html[data-theme="light"] [style*="background:#1a1510"]{background:#FFF8F0!important}
html[data-theme="light"] [style*="background:#1A1A1A"]{background:#F0EBE4!important}
html[data-theme="light"] [style*="border:1px solid #1E1E1E"]{border-color:#E8E2DA!important}
html[data-theme="light"] [style*="border:1px solid #2A2520"]{border-color:#E0D8D0!important}
html[data-theme="light"] [style*="border-bottom:1px solid #1A1A1A"]{border-bottom-color:#F0EBE4!important}
html[data-theme="light"] [style*="border-bottom:1px solid #1E1E1E"]{border-bottom-color:#F0EBE4!important}
html[data-theme="light"] [style*="border-bottom:1px solid #2A2520"]{border-bottom-color:#E0D8D0!important}
html[data-theme="light"] [style*="border-top:1px solid #2A2520"]{border-top-color:#E0D8D0!important}
html[data-theme="light"] [style*="color:#BFA164"]{color:#BFA164!important}
html[data-theme="light"] [style*="color:#50C878"]{color:#50C878!important}
html[data-theme="light"] [style*="color:#4FC3F7"]{color:#4FC3F7!important}
html[data-theme="light"] [style*="color:#FF6B6B"]{color:#FF6B6B!important}
html[data-theme="light"] [style*="color:#FF9800"]{color:#FF9800!important}
html[data-theme="light"] [style*="color:#CE93D8"]{color:#CE93D8!important}
</style>
<style scoped>
/* TOP BAR */
.sr-topbar { background:var(--fm-bg1,#141414)!important; border-bottom:1px solid var(--fm-br,#2A2520); padding:10px 0; position:sticky; top:0; z-index:1040; }
.sr-nav-link { color:var(--fm-m,#A09888)!important; text-decoration:none; font-size:14px; font-weight:500; }
.sr-nav-link:hover { color:#BFA164!important; }

/* SEARCH BAR */
.sr-search-bar { display:flex; align-items:center; background:var(--fm-bg1,#141414)!important; border:1px solid var(--fm-br,#2A2520); border-radius:8px; padding:2px 2px 2px 12px; min-width:400px; }
.sr-search-icon { color:var(--fm-s,#6B6356)!important; font-size:18px; margin-right:8px; }
.sr-search-input { background:transparent!important; border:none!important; color:var(--fm-h,#F5F0E8); font-size:14px; box-shadow:none!important; }
.sr-search-input::placeholder { color:var(--fm-s,#6B6356)!important; }
.sr-search-input-mobile { background:var(--fm-bg1,#141414); border:1px solid var(--fm-br,#2A2520)!important; color:var(--fm-h,#F5F0E8); font-size:14px; }
.sr-search-input-mobile::placeholder { color:var(--fm-s,#6B6356)!important; }
.sr-mobile-search { background:#0A0A0A!important; padding:8px 0; border-bottom:1px solid var(--fm-br,#2A2520); }

/* FILTERS PANEL */
.sr-filters-panel { background:var(--fm-bg1,#141414)!important; border:1px solid var(--fm-br,#2A2520); border-radius:14px; padding:20px; position:sticky; top:70px; }
.sr-filter-group { border-bottom:1px solid var(--fm-br,#2A2520); padding:12px 0; }
.sr-filter-group:last-of-type { border-bottom:none; }
.sr-filter-title { display:flex; justify-content:space-between; align-items:center; cursor:pointer; font-size:14px; font-weight:600; color:var(--fm-h,#F5F0E8)!important; }
.sr-filter-title:hover { color:#BFA164!important; }
.sr-filter-body { padding-top:10px; }
.sr-filter-option { display:flex; align-items:center; justify-content:space-between; padding:4px 0; font-size:13px; color:var(--fm-m,#A09888)!important; cursor:pointer; gap:8px; }
.sr-filter-option input { accent-color:#BFA164; margin-right:6px; flex-shrink:0; }
/* Radio/checkbox rows without a trailing count — left-align the label properly
   so text doesn't get pushed to the far right by space-between. */
.sr-filter-radio { display:flex; align-items:flex-start; gap:8px; padding:6px 0; font-size:13px; color:var(--fm-m,#A09888)!important; cursor:pointer; line-height:1.4; }
.sr-filter-radio input { accent-color:#BFA164; flex-shrink:0; margin-top:2px; }
.sr-filter-radio span { flex:1; word-break:break-word; }
.sr-filter-radio:hover { color:var(--fm-h,#F5F0E8)!important; }
.sr-filter-option:hover { color:var(--fm-h,#F5F0E8)!important; }
.sr-filter-count { font-size:11px; color:var(--fm-s,#6B6356)!important; margin-left:auto; }
.sr-filter-search { background:var(--fm-bg2,#0F0F0F); border:1px solid var(--fm-br,#2A2520)!important; color:var(--fm-h,#F5F0E8); font-size:13px; }
.sr-filter-search::placeholder { color:var(--fm-s,#6B6356)!important; }
.sr-filter-input { background:var(--fm-bg2,#0F0F0F); border:1px solid var(--fm-br,#2A2520)!important; color:var(--fm-h,#F5F0E8); font-size:13px; }
.sr-filter-input::placeholder { color:var(--fm-s,#6B6356)!important; }
.sr-price-chip { background:rgba(191,161,100,.08); color:var(--fm-m,#A09888)!important; border:1px solid var(--fm-br,#2A2520); font-size:11px; border-radius:50px; }
.sr-price-chip:hover { border-color:#BFA164; color:#BFA164!important; }

/* RESULTS HEADER */
.sr-results-header { display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:12px; margin-bottom:16px; padding:12px 16px; background:var(--fm-bg1,#141414)!important; border:1px solid var(--fm-br,#2A2520); border-radius:10px; }
.sr-results-count { font-size:14px; color:var(--fm-m,#A09888)!important; }
.sr-sort-select { background:var(--fm-bg2,#0F0F0F); border:1px solid var(--fm-br,#2A2520)!important; color:var(--fm-m,#A09888); font-size:13px; min-width:160px; }
.sr-sort-select:focus { box-shadow:none!important; }
.sr-view-toggle { display:flex; background:var(--fm-bg2,#0F0F0F)!important; border:1px solid var(--fm-br,#2A2520); border-radius:6px; flex-shrink:0; }
.sr-view-btn { background:transparent; border:none; color:var(--fm-s,#6B6356)!important; padding:6px 10px; cursor:pointer; font-size:16px; line-height:1; }
.sr-view-btn.active { background:rgba(191,161,100,.15); color:#BFA164!important; }
.sr-view-btn:hover { color:#BFA164!important; }

/* ACTIVE FILTER CHIPS */
.sr-active-chip { display:inline-flex; align-items:center; gap:6px; background:rgba(191,161,100,.1); color:#BFA164!important; padding:4px 12px; border-radius:50px; font-size:12px; font-weight:600; border:1px solid rgba(191,161,100,.2); }
.sr-active-chip .mdi-close { cursor:pointer; font-size:14px; opacity:0.7; }
.sr-active-chip .mdi-close:hover { opacity:1; }

/* GRID CARDS */
.sr-card-grid { background:var(--fm-bg1,#141414)!important; border:1px solid var(--fm-br,#2A2520); border-radius:14px; overflow:hidden; transition:all .3s; cursor:pointer; height:100%; }
.sr-card-grid:hover { border-color:#444; transform:translateY(-3px); }
.sr-card-img { position:relative; height:160px; background:linear-gradient(135deg,#1A1510,#0F0F0F); display:flex; align-items:center; justify-content:center; overflow:hidden; }
.sr-card-photo { width:100%; height:100%; object-fit:cover; }
.sr-card-placeholder { display:flex; align-items:center; justify-content:center; width:100%; height:100%; }
.sr-card-badges { position:absolute; top:8px; left:8px; display:flex; gap:4px; flex-wrap:wrap; }
.sr-badge-pill { font-size:10px; font-weight:700; padding:2px 7px; border-radius:10px; display:inline-flex; align-items:center; gap:3px; letter-spacing:.3px }
.sr-badge-pill i { font-size:11px; }
.sr-badge-pill.sb-star { background:rgba(80,200,120,.18); color:#1F6B1F; }
.sr-badge-pill.sb-on_track { background:rgba(102,178,255,.18); color:#0F4C81; }
.sr-badge-pill.sb-watch { background:rgba(241,180,76,.18); color:#8B5A2B; }
.sr-badge-pill.sb-at_risk { background:rgba(255,107,107,.18); color:#B71C1C; }
.sr-badge-pill.sb-establishing { background:rgba(160,152,136,.18); color:#5C4533; }
.sr-filter-option .sr-badge-pill { margin-left:4px; }
.sr-save-btn { position:absolute; top:8px; right:8px; width:30px; height:30px; background:rgba(0,0,0,.5); border:none; border-radius:50%; color:#fff!important; font-size:15px; display:flex; align-items:center; justify-content:center; cursor:pointer; }
.sr-save-btn:hover { background:rgba(0,0,0,.7); }
.sr-card-body { padding:12px; }
.sr-card-title { font-size:14px; font-weight:700; color:var(--fm-h,#F5F0E8)!important; margin-bottom:4px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.sr-card-meta { display:flex; gap:8px; font-size:11px; color:var(--fm-s,#6B6356)!important; margin-bottom:4px; flex-wrap:wrap; }
.sr-card-location { font-size:12px; color:var(--fm-m,#A09888)!important; margin-bottom:8px; }
.sr-card-footer { display:flex; justify-content:space-between; align-items:center; padding-top:8px; border-top:1px solid var(--fm-br,#2A2520); }
.sr-card-price { font-size:16px; font-weight:800; color:#BFA164!important; }
.sr-card-unit { font-size:11px; font-weight:500; color:var(--fm-s,#6B6356)!important; margin-left:4px; letter-spacing:.2px; }
.sr-card-seller { display:flex; align-items:center; gap:3px; font-size:11px; color:var(--fm-s,#6B6356)!important; }
.sr-seller-avatar { width:18px; height:18px; background:#BFA164!important; color:#0A0A0A!important; border-radius:50%; font-size:8px; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; }

/* LIST CARDS */
.sr-card-list { background:var(--fm-bg1,#141414)!important; border:1px solid var(--fm-br,#2A2520); border-radius:14px; overflow:hidden; display:flex; cursor:pointer; transition:all .3s; }
.sr-card-list:hover { border-color:#444; }
.sr-list-img { position:relative; width:200px; min-height:140px; flex-shrink:0; background:linear-gradient(135deg,#1A1510,#0F0F0F); display:flex; align-items:center; justify-content:center; overflow:hidden; }
.sr-list-photo { width:100%; height:100%; object-fit:cover; }
.sr-list-placeholder { display:flex; align-items:center; justify-content:center; width:100%; height:100%; }
.sr-list-body { flex:1; padding:16px; }

/* PAGINATION */
.sr-pagination { display:flex; align-items:center; justify-content:center; flex-wrap:wrap; gap:4px; margin-top:24px; padding-top:20px; border-top:1px solid var(--fm-br,#2A2520); }
.sr-page-btn { width:36px; height:36px; border-radius:8px; border:1px solid var(--fm-br,#2A2520); background:var(--fm-bg1,#141414)!important; color:var(--fm-m,#A09888)!important; font-size:14px; font-weight:600; display:flex; align-items:center; justify-content:center; cursor:pointer; transition:all .2s; }
.sr-page-btn:hover { border-color:#BFA164; color:#BFA164!important; }
.sr-page-btn.active { background:#BFA164!important; color:#0A0A0A!important; border-color:#BFA164; }
.sr-page-btn:disabled { opacity:0.4; cursor:not-allowed; }

/* SOLD-SHOWCASE — diagonal SOLD watermark + muted card chrome.
 * Watermark sits ABOVE the photo (z-index 2) but below the badges/buttons (z-index 3+).
 * The card itself is desaturated so it reads clearly as historical/social-proof rather than active inventory. */
.sr-card-sold { opacity:0.92; }
.sr-card-sold .sr-card-photo,
.sr-card-sold .sr-list-photo { filter:grayscale(0.35) brightness(0.9); }
.sr-sold-overlay {
  position:absolute; top:0; left:0; right:0; bottom:0;
  display:flex; align-items:center; justify-content:center;
  background:rgba(0,0,0,0.18);
  pointer-events:none;
  z-index:2;
}
.sr-sold-stamp {
  font-size:42px; font-weight:900; letter-spacing:4px;
  color:#FF3B3B;
  text-shadow:0 2px 8px rgba(0,0,0,0.65), 0 0 1px #000;
  transform:rotate(-15deg);
  border:4px solid #FF3B3B;
  padding:4px 18px;
  border-radius:6px;
  background:rgba(255,255,255,0.08);
}
.sr-sold-stamp-list { font-size:28px; padding:2px 12px; border-width:3px; letter-spacing:3px; }
.sr-sold-meta { margin-top:8px; padding-top:8px; border-top:1px dashed var(--fm-br,#2A2520); }
.sr-sold-caption { font-size:11px; color:var(--fm-s,#6B6356); font-style:italic; }
.sr-sold-hide { display:inline-block; margin-top:4px; font-size:11px; color:#BFA164; text-decoration:none; }
.sr-sold-hide:hover { text-decoration:underline; color:#D4B97A; }

/* RESPONSIVE */
@media (max-width: 991px) {
  .sr-search-bar { min-width:auto; flex:1; }
  .sr-list-img { width:140px; min-height:120px; }
}
@media (max-width: 575px) {
  .sr-list-img { width:100px; min-height:100px; }
  .sr-card-img { height:130px; }
  .sr-sold-stamp { font-size:30px; padding:2px 12px; }
}
</style>
